// src/components/SuperAdmin/DeveloperDashboard.js
import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase'; // your initialized Firestore instance
import { Backsheet, Button, FormInput } from '../ui/UIComponents';

export default function DeveloperDashboard() {
    const [developers, setDevelopers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        slug: '',
        name: '',
        logo: '',
        description: '',
        sequence: ''
    });
    const [originalSlug, setOriginalSlug] = useState('');

    // Load devs from propxplorer/developers
    const fetchDevelopers = async () => {
        setLoading(true);
        try {
            const ref = doc(db, 'propxplorer', 'developers');
            const snap = await getDoc(ref);
            let list = [];
            if (snap.exists()) {
                const data = snap.data();
                if (Array.isArray(data.developers)) {
                    // new array model
                    list = data.developers;
                } else {
                    // old slug-keyed model
                    list = Object.entries(data).map(([slug, dev]) => ({ slug, ...dev }));
                }
                list.sort((a, b) => Number(a.sequence) - Number(b.sequence));
            }
            setDevelopers(list);
        } catch (e) {
            console.error('Fetch failed', e);
            setDevelopers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevelopers();
    }, []);

    const openForEdit = dev => {
        setFormData({ ...dev });
        setOriginalSlug(dev.slug);
        setIsOpen(true);
    };

    const openForAdd = () => {
        setFormData({ slug: '', name: '', logo: '', description: '', sequence: '' });
        setOriginalSlug('');
        setIsOpen(true);
    };

    const handleSave = async () => {
        const { slug, name, logo, description, sequence } = formData;
        if (!slug.trim()) {
            alert('Slug is required');
            return;
        }
        // ensure all fields are filled
        if (!name.trim() || !logo.trim() || !description.trim() || !sequence.toString().trim()) {
            alert('All fields are required.');
            return;
        }
        // ensure slug uniqueness (allow originalSlug during edit)
        const slugExists = developers.some(d => d.slug === slug);
        if (slugExists && slug !== originalSlug) {
            alert('Slug must be unique.');
            return;
        }

        const payload = { slug, name, logo, description, sequence };
        try {
            // 1) Upsert individual doc
            await setDoc(doc(db, 'developers', slug), payload, { merge: true });

            // 2) Update array in propxplorer/developers
            const proRef = doc(db, 'propxplorer', 'developers');
            const proSnap = await getDoc(proRef);
            let arr = [];
            if (proSnap.exists() && Array.isArray(proSnap.data().developers)) {
                arr = proSnap.data().developers;
            }
            const idx = arr.findIndex(d => d.slug === slug);
            if (idx > -1) arr[idx] = payload;
            else arr.push(payload);
            await setDoc(proRef, { developers: arr });

            setIsOpen(false);
            fetchDevelopers();
        } catch (e) {
            console.error('Save failed', e);
            alert('Save failed: ' + e.message);
        }
    };

    const handleDelete = async slugToDelete => {
        if (!window.confirm(`Delete developer '${slugToDelete}'?`)) return;
        try {
            // 1) Remove individual doc
            await deleteDoc(doc(db, 'developers', slugToDelete));

            // 2) Remove from array
            const proRef = doc(db, 'propxplorer', 'developers');
            const proSnap = await getDoc(proRef);
            let arr = [];
            if (proSnap.exists() && Array.isArray(proSnap.data().developers)) {
                arr = proSnap.data().developers.filter(d => d.slug !== slugToDelete);
            }
            await setDoc(proRef, { developers: arr });

            setIsOpen(false);
            fetchDevelopers();
        } catch (e) {
            console.error('Delete failed', e);
            alert('Delete failed: ' + e.message);
        }
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(fd => ({ ...fd, [name]: value }));
    };

    return (
        <div className="min-h-screen">
            <header className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Developer Dashboard</h2>
                <Button onClick={openForAdd}>Add Developer</Button>
            </header>

            {loading ? (
                <p className="text-center">Loading developers…</p>
            ) : developers.length === 0 ? (
                <p className="text-center">No developers found. Click “Add Developer” to get started.</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {developers.map(dev => (
                        <div key={dev.slug} className="bg-white p-4 rounded shadow">
                            {dev.logo && (
                                <img
                                    src={dev.logo}
                                    alt={dev.name}
                                    className="h-16 w-16 object-contain mb-2"
                                />
                            )}
                            <h3 className="text-xl font-medium">{dev.name || dev.slug}</h3>
                            <p className="text-sm text-gray-500">Sequence: {dev.sequence}</p>
                            <p className="mt-2 text-gray-700 text-sm">
                                {dev.description
                                    ? dev.description.slice(0, 100) + '…'
                                    : 'No description.'}
                            </p>
                            <Button
                                onClick={() => openForEdit(dev)}
                                variant="secondary"
                                className="mt-4"
                            >
                                Edit
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <Backsheet
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={formData.slug ? 'Edit Developer' : 'Add Developer'}
            >
                <div className="space-y-4">
                    <FormInput
                        label="Slug"
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Logo URL"
                        name="logo"
                        value={formData.logo}
                        onChange={handleChange}
                        required
                    />
                    <FormInput
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        textarea
                        required
                    />
                    <FormInput
                        label="Sequence"
                        name="sequence"
                        type="number"
                        value={formData.sequence}
                        onChange={handleChange}
                        required
                    />
                    <div className="flex justify-between">
                        {formData.slug && (
                            <Button
                                variant="danger"
                                onClick={() => handleDelete(formData.slug)}
                            >
                                Delete
                            </Button>
                        )}
                        <div className="ml-auto flex space-x-2">
                            <Button variant="secondary" onClick={() => setIsOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave}>Save</Button>
                        </div>
                    </div>
                </div>
            </Backsheet>
        </div>
    );
}
